import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sun, Zap, TrendingUp, Battery, Leaf, DollarSign, Users, Award, BarChart3, Target, Shield } from 'lucide-react';
import NavigationHeader from '../homepage/components/NavigationHeader';
import FooterSection from '../homepage/components/FooterSection';

export default function SolarFarmsInitiative() {
  const [selectedMetric, setSelectedMetric] = useState('energy');

  const energyMetrics = [
  {
    id: 1,
    title: "Current Energy Production",
    value: "2.8 MW",
    trend: "+15%",
    icon: Zap,
    description: "Real-time solar energy generation capacity",
    color: "text-yellow-600"
  },
  {
    id: 2,
    title: "Carbon Offset",
    value: "1,247 tons",
    trend: "+22%",
    icon: Leaf,
    description: "Annual CO2 emissions prevented",
    color: "text-green-600"
  },
  {
    id: 3,
    title: "Cost Savings",
    value: "₱8.4M",
    trend: "+18%",
    icon: DollarSign,
    description: "Annual savings from renewable energy",
    color: "text-blue-600"
  },
  {
    id: 4,
    title: "Grid Independence",
    value: "67%",
    trend: "+12%",
    icon: Battery,
    description: "Park operations powered by solar",
    color: "text-purple-600"
  }];


  const solarInstallations = [
  {
    id: 1,
    name: "Visitor Center Solar Array",
    location: "Main Entrance Complex",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b742a1f-1765104623062.png",
    alt: "Large solar panel array installed on visitor center rooftop with mountains visible in background, modern renewable energy infrastructure",
    capacity: "850 kW",
    panels: 2400,
    commissioned: "2021",
    powersSupply: ["Visitor facilities", "Administrative offices", "Interpretation center", "Gift shop operations"],
    annualOutput: "1.2 GWh",
    impact: "Reduces 420 tons CO2 annually"
  },
  {
    id: 2,
    name: "Research Station Microgrid",
    location: "Lowland Forest Zone",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14fc31f3a-1764670836814.png",
    alt: "Solar panels integrated with forest research station building, sustainable energy system supporting scientific operations in remote location",
    capacity: "320 kW",
    panels: 900,
    commissioned: "2022",
    powersSupply: ["Research laboratories", "Data collection systems", "Climate monitoring equipment", "Living quarters"],
    annualOutput: "450 MWh",
    impact: "Enables off-grid research operations"
  },
  {
    id: 3,
    name: "Ranger Outpost Network",
    location: "Multiple Patrol Stations",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16270ecb4-1765345789572.png",
    alt: "Small solar panel installation at remote ranger station in forest, portable renewable energy system supporting conservation patrol operations",
    capacity: "180 kW",
    panels: 500,
    commissioned: "2023",
    powersSupply: ["Communication systems", "GPS tracking equipment", "Emergency lighting", "Water pumping stations"],
    annualOutput: "240 MWh",
    impact: "Powers 8 remote ranger stations"
  },
  {
    id: 4,
    name: "Educational Trail Lighting",
    location: "Nature Walk Pathways",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c053f5c8-1765345789067.png",
    alt: "Solar-powered LED lights illuminating nature trail at dusk, sustainable lighting solution for visitor safety in protected area",
    capacity: "45 kW",
    panels: 120,
    commissioned: "2023",
    powersSupply: ["Trail lighting systems", "Emergency call boxes", "Interpretive displays", "Night tour facilities"],
    annualOutput: "60 MWh",
    impact: "Safe nighttime wildlife observation"
  }];


  const technicalFeatures = [
  {
    icon: Shield,
    title: "Storm-Resistant Design",
    description: "Engineered to withstand typhoon-force winds and heavy rainfall common in tropical climate"
  },
  {
    icon: Battery,
    title: "Energy Storage Systems",
    description: "Advanced battery banks store excess energy for nighttime operations and cloudy weather"
  },
  {
    icon: Users,
    title: "Smart Grid Integration",
    description: "Automated load balancing and priority power allocation for critical conservation systems"
  },
  {
    icon: Award,
    title: "Maintenance Training",
    description: "Local technicians trained in solar system maintenance, creating green jobs in the community"
  }];


  const sustainabilityGoals = [
  {
    goal: "100% Renewable Energy",
    currentProgress: 67,
    targetYear: 2030,
    description: "All park operations powered entirely by renewable sources"
  },
  {
    goal: "Zero Carbon Footprint",
    currentProgress: 55,
    targetYear: 2032,
    description: "Complete elimination of fossil fuel dependence in park management"
  },
  {
    goal: "Community Solar Access",
    currentProgress: 28,
    targetYear: 2035,
    description: "Extend solar power benefits to adjacent communities through micro-grids"
  }];


  const environmentalBenefits = [
  {
    title: "Reduced Diesel Consumption",
    value: "45,000 liters",
    description: "Annual diesel fuel eliminated from generator operations"
  },
  {
    title: "Noise Pollution Reduction",
    value: "95%",
    description: "Silent solar operation vs. loud diesel generators improves wildlife habitat"
  },
  {
    title: "Habitat Preservation",
    value: "12 hectares",
    description: "Land area no longer needed for fuel storage and generator infrastructure"
  },
  {
    title: "Water Conservation",
    value: "180,000L",
    description: "Annual water savings from eliminating generator cooling requirements"
  }];


  const futureExpansion = [
  {
    project: "Marine Station Solar Integration",
    timeline: "2025-2026",
    capacity: "500 kW",
    description: "Solar power for coastal monitoring stations and marine research facilities"
  },
  {
    project: "Electric Vehicle Charging Network",
    timeline: "2026-2027",
    capacity: "200 kW",
    description: "Solar-powered EV charging for park patrol vehicles and visitor shuttles"
  },
  {
    project: "Community Micro-Grid Expansion",
    timeline: "2027-2030",
    capacity: "1.5 MW",
    description: "Extend solar power access to buffer zone communities to reduce forest pressure"
  }];


  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-orange-50 dark:from-gray-900 dark:to-gray-800">
      <NavigationHeader />
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden mt-20">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_11184ab41-1765094971396.png"
          alt="Expansive solar farm installation with rows of photovoltaic panels reflecting sunlight, mountains and forest in background showing integration of renewable energy with conservation landscape"
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
          <div className="max-w-7xl mx-auto px-4 h-full flex flex-col justify-end pb-16">
            <Link to="/site-showcase-gallery" className="flex items-center space-x-2 text-white/90 hover:text-white mb-4 w-fit">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Site Showcase</span>
            </Link>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Solar Farms Initiative
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Powering conservation with clean energy - integrating renewable technology to reduce environmental impact and fund protection efforts
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Key Metrics Dashboard */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {energyMetrics?.map((metric) =>
          <div key={metric?.id} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-full bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30`}>
                  <metric.icon className={`h-6 w-6 ${metric?.color}`} />
                </div>
                <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                  {metric?.trend}
                </span>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
                {metric?.value}
              </h3>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {metric?.title}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {metric?.description}
              </p>
            </div>
          )}
        </div>

        {/* Real-Time Energy Dashboard */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl mb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
                Live Energy Production
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Current solar generation and system performance
              </p>
            </div>
            <Sun className="h-12 w-12 text-yellow-500 animate-pulse" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 rounded-xl bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20">
              <Zap className="h-10 w-10 text-yellow-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-gray-800 dark:text-white mb-1">2,247 kW</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Current Output</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
              <TrendingUp className="h-10 w-10 text-blue-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-gray-800 dark:text-white mb-1">18,450 kWh</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Today's Generation</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
              <BarChart3 className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-gray-800 dark:text-white mb-1">92%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">System Efficiency</div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 rounded-xl p-6">
            <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
              <span>Peak sun hours today: 6.2 hours</span>
              <span>Weather: Partly cloudy</span>
              <span>Panel temperature: 45°C</span>
            </div>
          </div>
        </div>

        {/* Solar Installations */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">
            Our Solar Installations
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {solarInstallations?.map((installation) =>
            <div key={installation?.id} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
                <div className="relative h-64 overflow-hidden">
                  <img
                  src={installation?.image}
                  alt={installation?.alt}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />

                  <div className="absolute top-4 right-4 bg-yellow-500 text-white px-4 py-2 rounded-full font-semibold">
                    {installation?.capacity}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                    {installation?.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex items-center">
                    <Target className="h-4 w-4 mr-2" />
                    {installation?.location}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Panels</div>
                      <div className="text-lg font-bold text-gray-800 dark:text-white">{installation?.panels}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Commissioned</div>
                      <div className="text-lg font-bold text-gray-800 dark:text-white">{installation?.commissioned}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Annual Output</div>
                      <div className="text-lg font-bold text-gray-800 dark:text-white">{installation?.annualOutput}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Impact</div>
                      <div className="text-xs font-semibold text-green-600 dark:text-green-400">{installation?.impact}</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Powers:</h4>
                    <ul className="space-y-1">
                      {installation?.powersSupply?.map((item, index) =>
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start">
                          <Zap className="h-4 w-4 mr-2 text-yellow-600 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Technical Features */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">
            Advanced Solar Technology
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {technicalFeatures?.map((feature, index) =>
            <div key={index} className="flex items-start space-x-4 p-6 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors">
                <div className="bg-yellow-500 text-white p-3 rounded-full flex-shrink-0">
                  <feature.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                    {feature?.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {feature?.description}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Environmental Benefits */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 shadow-xl text-white mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Environmental Impact</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {environmentalBenefits?.map((benefit, index) =>
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="text-3xl font-bold mb-2">{benefit?.value}</div>
                <h3 className="text-lg font-semibold mb-2">{benefit?.title}</h3>
                <p className="text-sm text-white/80">{benefit?.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sustainability Goals */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">
            Our Sustainability Roadmap
          </h2>
          <div className="space-y-6">
            {sustainabilityGoals?.map((goal, index) =>
            <div key={index} className="border-b border-gray-200 dark:border-gray-700 last:border-0 pb-6 last:pb-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">{goal?.goal}</h3>
                  <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Target: {goal?.targetYear}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{goal?.description}</p>
                <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                  style={{ width: `${goal?.currentProgress}%` }} />

                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Current Progress</span>
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">{goal?.currentProgress}%</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Future Expansion */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">
            Future Expansion Plans
          </h2>
          <div className="space-y-6">
            {futureExpansion?.map((project, index) =>
            <div key={index} className="flex items-start space-x-6 p-6 rounded-xl border-2 border-yellow-200 dark:border-yellow-800 hover:border-yellow-400 dark:hover:border-yellow-600 transition-colors">
                <div className="bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 p-4 rounded-full">
                  <Sun className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">{project?.project}</h3>
                    <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">{project?.capacity}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Timeline: {project?.timeline}</p>
                  <p className="text-gray-700 dark:text-gray-300">{project?.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Educational Resources */}
        <div className="mt-16 bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 rounded-2xl p-8 shadow-xl">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 text-center">
            Learn About Solar Energy
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-xl bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <Sun className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Solar Tours</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Visit our installations with technical guides</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <Users className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Workshops</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Renewable energy education programs</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 transition-colors">
              <Award className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Research Data</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Access energy production and impact reports</p>
            </div>
          </div>
        </div>
      </div>
      <FooterSection />
    </div>);

}